import { Box, Menu, MenuContent, MenuTrigger, MenuItem, Tab } from '@cad-arc';
import useOnClickOutside from 'use-onclickoutside';
import { NotificationCounter } from '../../../common/components/notification-counter';
import { useMessagingStore } from '../../hooks/use-messaging-store';
import { ConversationParticipantView } from '@mark43/cad-api';
import { useNewConversationCancellationModal } from '../../hooks/use-new-conversation-cancellation-modal';
import { FilterTabMenuType } from '../../types';
import { observer } from 'mobx-react-lite';
import React, { useRef } from 'react';
import styles from './filter-tab.module.css';
import { useApplicationType } from '../../../common/hooks/use-application-type';
import { buildParticipantKey } from '../../store/helpers';
import { participantKeysMatch } from '../../helpers/conversations-filter-helpers';

export const ConversationFilterTabMenu: React.FC<{
    label: string;
    filterTabMenuType: FilterTabMenuType;
    participants: ConversationParticipantView[];
    isMenuOpen: boolean;
    clearMenuOpenShortcutPress: () => void;
    setShowAllConversations?: (value: boolean) => void;
}> = observer(
    ({
        participants,
        label,
        filterTabMenuType,
        isMenuOpen,
        clearMenuOpenShortcutPress,
        setShowAllConversations,
    }) => {
        const {
            participantFiltersInfo,
            dispatchAreasUnreadInfo,
            selectedConversationFilter,
            setSelectedConversationFilter,
            setActiveFilterTabMenu,
            isCreatingNewConversation,
            isBookmarksView,
        } = useMessagingStore();
        const {
            setIsOpen,
            updateConfirmCancellationStoredData,
        } = useNewConversationCancellationModal();
        const { isFirstResponder } = useApplicationType();

        const menuContentRef = useRef<HTMLDivElement>(null);
        const groupTabButtonRef = useRef<HTMLDivElement>(null);

        useOnClickOutside(menuContentRef, (event) => {
            if (
                event?.target instanceof HTMLElement &&
                groupTabButtonRef.current?.contains(event.target)
            ) {
                // skip onClickOutside if click target is group tab Menu or its sub-component (svg).
                // If onClickOutside is also triggered, it will close the menu, but menu click will toggle menu open again.
                return;
            }
            if (isMenuOpen) {
                setActiveFilterTabMenu({ tab: filterTabMenuType, isOpen: false });
            }
            // shortcut ctrl key may not be released/cleared when switch to other window and switch back,
            clearMenuOpenShortcutPress();
        });

        if (
            // don't show filter group button if user doesn't have this participant identity type
            !participants ||
            participants.length === 0 ||
            // data hasn't loaded yet
            participantFiltersInfo === undefined ||
            dispatchAreasUnreadInfo === undefined
        ) {
            return <></>;
        }

        return (
            <Menu isOpen={isMenuOpen}>
                <MenuTrigger
                    tabIndex={-1}
                    isDisabled={isBookmarksView}
                    onClick={() => {
                        if (isMenuOpen) {
                            setActiveFilterTabMenu({ tab: filterTabMenuType, isOpen: false });
                        } else {
                            setActiveFilterTabMenu({ tab: filterTabMenuType, isOpen: true });
                        }
                    }}
                >
                    <Tab
                        ref={groupTabButtonRef}
                        tabIndex={-1}
                        onKeyDown={(keyboardEvent: KeyboardEvent) => {
                            if (keyboardEvent.key === 'Enter') {
                                keyboardEvent.preventDefault();
                            }
                        }}
                        isDisabled={isBookmarksView}
                        data-testid="conversation-filter-group"
                        value={filterTabMenuType}
                        trailingVisual="Open"
                        leadingVisual={
                            filterTabMenuType === 'DISPATCH_AREA' &&
                            !!dispatchAreasUnreadInfo.unreadCount && (
                                <Box marginRight={1}>
                                    <NotificationCounter
                                        isUrgent={dispatchAreasUnreadInfo.hasUrgentUnread}
                                    >
                                        {dispatchAreasUnreadInfo.unreadCount}
                                    </NotificationCounter>
                                </Box>
                            )
                        }
                        className={styles['tab']}
                    >
                        {label}
                    </Tab>
                </MenuTrigger>
                <MenuContent ref={menuContentRef}>
                    {participants.map((participant) => {
                        const { entityType, id } = participant;
                        const participantKey = buildParticipantKey(id, entityType);
                        const { unreadCount = 0, hasUrgentUnread = false } =
                            participantFiltersInfo?.[participantKey] ?? {};
                        const isSelected = participantKeysMatch(
                            participant,
                            selectedConversationFilter.filterParticipant
                        );
                        return (
                            <MenuItem
                                data-testid="conversation-filter"
                                {...(isSelected ? { 'data-isselected': true } : undefined)}
                                key={participantKey}
                                tabIndex={-1}
                                onSelect={() => {
                                    if (isFirstResponder && setShowAllConversations) {
                                        setShowAllConversations(true);
                                    }
                                    setActiveFilterTabMenu({
                                        tab: filterTabMenuType,
                                        isOpen: false,
                                    });
                                    const filter = {
                                        filterType: filterTabMenuType,
                                        filterParticipant: participant,
                                    };
                                    if (isCreatingNewConversation) {
                                        updateConfirmCancellationStoredData?.(filter);
                                        setIsOpen?.(true);
                                    } else {
                                        setSelectedConversationFilter(filter);
                                    }
                                }}
                                leadingVisual={
                                    filterTabMenuType === 'DISPATCH_AREA' &&
                                    !!unreadCount && (
                                        <Box marginRight={1} data-testid="unread-counter">
                                            <NotificationCounter isUrgent={hasUrgentUnread}>
                                                {unreadCount}
                                            </NotificationCounter>
                                        </Box>
                                    )
                                }
                            >
                                <span
                                    style={
                                        isSelected
                                            ? {
                                                  color: 'var(--arc-colors-brand-default)',
                                                  textDecoration: 'underline',
                                                  textDecorationColor:
                                                      'var(--arc-colors-brand-default)',
                                                  textDecorationThickness: '2px',
                                              }
                                            : {}
                                    }
                                >
                                    {participant.label}
                                </span>
                            </MenuItem>
                        );
                    })}
                </MenuContent>
            </Menu>
        );
    }
);
